import { createContext, useContext, useEffect, useRef } from 'react'

export const SheetPresentationContext = createContext('overlay')

export default function Sheet(props) {
  const presentation = useContext(SheetPresentationContext)
  if (presentation === 'inline') return <InlineSheet {...props} />
  return <OverlaySheet {...props} />
}

function InlineSheet({ title, children }) {
  return (
    <section className="split-detail-surface" aria-label={title} tabIndex={-1}>
      <h2>{title}</h2>
      <SheetPresentationContext.Provider value="overlay">
        {children}
      </SheetPresentationContext.Provider>
    </section>
  )
}

/** Bottom sheet. Escape closes it, backdrop click closes it. */
function OverlaySheet({ title, onClose, children }) {
  const ref = useRef(null)
  const closeRef = useRef(onClose)

  useEffect(() => {
    closeRef.current = onClose
  }, [onClose])

  useEffect(() => {
    const previousFocus = document.activeElement

    function focusable() {
      if (!ref.current) return []
      return Array.from(ref.current.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      )).filter((el) => el.getClientRects().length > 0)
    }

    /* Every mounted sheet listens on document, so each one has to check it owns
       the topmost backdrop before acting on anything. Without this the sheet
       underneath fights the sheet above it. On iOS the payout detail and the
       payable editor are siblings rather than nested, so the lower one read
       every focus inside the upper one as "outside me" and pulled it back,
       which made every field but the first one impossible to focus. */
    function isTopmost() {
      const own = ref.current?.closest('.sheet-backdrop')
      if (!own) return false
      const visible = Array.from(document.querySelectorAll('.sheet-backdrop'))
        .filter((element) => element.getClientRects().length > 0)
      return visible.at(-1) === own
    }

    function onKey(e) {
      if (!isTopmost()) return
      if (e.key === 'Escape') {
        e.preventDefault()
        e.stopImmediatePropagation()
        closeRef.current()
        return
      }

      if (e.metaKey && e.key === 'Enter' && !e.repeat && ref.current?.closest('.app.mac')) {
        const primary = Array.from(ref.current.querySelectorAll('[data-sheet-primary]'))
          .filter((el) => !el.disabled && el.getAttribute('aria-disabled') !== 'true' && el.getClientRects().length > 0)
        if (primary.length === 1) {
          e.preventDefault()
          primary[0].click()
        }
        return
      }

      if (e.key !== 'Tab') return
      const controls = focusable()
      if (!controls.length) {
        e.preventDefault()
        ref.current?.focus()
        return
      }
      const first = controls[0]
      const last = controls[controls.length - 1]
      if (e.shiftKey && (document.activeElement === first || !ref.current?.contains(document.activeElement))) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }

    function onFocus(e) {
      if (!isTopmost()) return
      if (ref.current?.contains(e.target)) return
      const first = focusable()[0]
      ;(first || ref.current)?.focus()
    }

    document.addEventListener('keydown', onKey)
    document.addEventListener('focusin', onFocus)
    const first = focusable()[0]
    ;(first || ref.current)?.focus()
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('focusin', onFocus)
      if (previousFocus instanceof HTMLElement && previousFocus.isConnected) previousFocus.focus()
    }
  }, [])

  /* Drag the sheet down to close it, the way every iOS sheet works. The rule
     that makes it feel right rather than fight the content: a downward drag
     only becomes a dismiss when the sheet is already scrolled to its top.
     Anywhere else it is an ordinary scroll. Listeners are native rather than
     React props because React attaches touchmove passively, which would make
     preventDefault a no-op. */
  useEffect(() => {
    const el = ref.current
    if (!el || !el.closest('.app.ios')) return undefined

    let drag = null

    function onTouchStart(event) {
      if (event.touches.length !== 1) return
      /* A sheet opened on top of this one sits inside it in the DOM, so its
         touches bubble up here too. Only the sheet actually under the finger
         may drag, or closing an entry's actions would drag the note away. */
      if (event.target.closest?.('.sheet') !== el) {
        drag = null
        return
      }
      el.classList.remove('is-settling')
      drag = { startY: event.touches[0].clientY, atTop: el.scrollTop <= 0, locked: false, dy: 0 }
    }

    function onTouchMove(event) {
      if (!drag || event.touches.length !== 1) return
      const dy = event.touches[0].clientY - drag.startY
      if (!drag.locked) {
        /* Upward, or anywhere but the top, belongs to the scroller. */
        if (dy <= 0 || !drag.atTop || el.scrollTop > 0) {
          if (Math.abs(dy) > 8) drag = null
          return
        }
        if (dy < 10) return
        drag.locked = true
        el.classList.add('is-dragging')
      }
      event.preventDefault()
      drag.dy = Math.max(0, dy)
      el.style.transform = 'translateY(' + drag.dy + 'px)'
    }

    function onTouchEnd() {
      const finished = drag
      drag = null
      if (!finished || !finished.locked) return
      el.classList.remove('is-dragging')
      el.classList.add('is-settling')
      if (finished.dy > el.offsetHeight * 0.25) {
        closeRef.current()
        return
      }
      el.style.transform = ''
    }

    el.addEventListener('touchstart', onTouchStart, { passive: true })
    el.addEventListener('touchmove', onTouchMove, { passive: false })
    el.addEventListener('touchend', onTouchEnd)
    el.addEventListener('touchcancel', onTouchEnd)
    return () => {
      el.removeEventListener('touchstart', onTouchStart)
      el.removeEventListener('touchmove', onTouchMove)
      el.removeEventListener('touchend', onTouchEnd)
      el.removeEventListener('touchcancel', onTouchEnd)
    }
  }, [])

  return (
    <div
      className="sheet-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) {
          e.preventDefault()
          onClose()
        }
      }}
    >
      <div className="sheet" role="dialog" aria-modal="true" aria-label={title} ref={ref} tabIndex={-1}>
        <div className="grabber" />
        <h2>{title}</h2>
        {children}
      </div>
    </div>
  )
}
